// Cache version checker — compares the version loaded into this tab against
// the version currently deployed in production, so users can be prompted to
// reload when they are running stale bundles.

export const CACHE_VERSION_POLL_INTERVAL_MS = 60000; //60 seconds in milliseconds

type RuntimeEnvironment = { REACT_APP_VERSION?: string };

function readRuntimeVersion(): string {
  const env = (window as unknown as { __environment__?: RuntimeEnvironment })
    .__environment__;
  return env?.REACT_APP_VERSION ?? '';
}

// Version baked into the bundle that is currently running in this tab
export const CACHED_APP_VERSION: string = readRuntimeVersion();

// Fetch the production version. Returns `null` on any
// failure so the caller can treat the check as "unknown" rather than
// triggering a false-positive reload prompt.
export async function fetchProductionVersion(): Promise<string | null> {
  try {
    //pass in date to prevent CDN caching
    const response = await fetch(`/runtimeConfig.js?_=${Date.now()}`, {
      cache: 'no-store',
    });

    if (!response.ok) return null;
    const text = await response.text();
    const match = text.match(/REACT_APP_VERSION:\s*['"]([^'"]+)['"]/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

export function isVersionMismatch(
  cached: string,
  latest: string | null,
): boolean {
  if (!latest || !cached) return false;
  return cached !== latest;
}

export async function clearCachesAndReload(): Promise<void> {
  try {
    if (typeof window !== 'undefined' && 'caches' in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((key) => window.caches.delete(key)));
    }
  } catch {
    // ignore cache errors
  }

  // index.html loads /runtimeConfig.js via a <script> tag with no cache-buster,
  // so the browser HTTP cache will serve the stale copy on the next page load
  // and CACHED_APP_VERSION would re-read the old version — making the update
  // dialog reappear after reload. Pre-populate the cache with a fresh copy.
  try {
    await fetch('/runtimeConfig.js', { cache: 'reload' });
  } catch {
    // ignore — reload still proceeds
  }

  // Force fresh asset fetch
  const url = new URL(window.location.href);
  url.searchParams.set('_v', Date.now().toString());

  window.location.replace(url.toString());
}
